// useDivesites.js
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useDivesitesContext } from "./useDivesitesContext";
import { checkItemLiked } from "../utils/likeApi"; // Assuming this utility function is for frontend check

/**
 * Custom hook to handle all divesite data fetching and filtering logic.
 * It interacts with the backend's paginated and filtered divesites endpoint.
 */
const useDivesites = () => {
  const { divesites: contextDivesites, dispatch } = useDivesitesContext();
  const authContext = useAuth();
  const userId = authContext?.currentUser?.uid; // Firebase UID
  const userObjectId = authContext?.userObjectId; // MongoDB _id of the authenticated user

  console.log(`[useDivesites Init] Current Firebase userId (uid): ${userId}`);
  console.log(
    `[useDivesites Init] Current MongoDB userObjectId: ${userObjectId}`
  );

  // State for pagination and loading
  const [state, setState] = useState({
    hasMore: true, // True if there are more pages to load from the backend
    pageNumber: 1, // Current page number to request
    loading: false, // True when data is being fetched
    error: null, // Stores any error messages
    initialLoading: true, // True for the very first load or when filters are reset
    isLoadMorePending: false, // True when a "load more" request is in progress
    allDivesites: [], // Accumulates all divesites fetched from the backend (already filtered by backend)
  });

  // Filter state managed by this hook, will be sent to the backend
  const [filter, setFilter] = useState({
    diveType: "",
    city: "",
    state: "",
    liked: false, // Represents the 'likedBy' filter for the current user
  });

  // Filter options state (e.g., for dropdowns), populated from fetched divesites
  const [filterOptions, setFilterOptions] = useState({
    uniqueDiveTypes: [],
    uniqueCities: [],
    uniqueStates: [],
  });

  // Destructure state for easier access
  const {
    hasMore,
    pageNumber,
    loading,
    error,
    initialLoading,
    isLoadMorePending,
    allDivesites,
  } = state;

  // Derived state to check if any filter is active for UI purposes
  const isFilterActive =
    filter.diveType !== "" ||
    filter.city !== "" ||
    filter.state !== "" ||
    filter.liked;

  // --- Core Data Fetching Function ---
  const fetchDivesites = useCallback(
    async (fetchPageNumber, currentFilters) => {
      // Prevent multiple concurrent fetches
      if (loading) {
        console.log(
          `[fetchDivesites] Already loading, skipping fetch for page ${fetchPageNumber}.`
        );
        return;
      }

      setState((prev) => ({ ...prev, loading: true, error: null }));

      try {
        console.log(
          `[fetchDivesites START] Attempting to fetch page ${fetchPageNumber} with filters:`,
          currentFilters
        );

        // Get authentication token for API calls
        const token = userId
          ? await authContext.currentUser.getIdToken()
          : null;

        const payloadHeader = {
          headers: {
            "Content-Type": "application/json",
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        };

        // Construct API URL with pagination and filters for the backend
        let apiUrl = `/api/divesites/filtered-paginated?page=${fetchPageNumber}&limit=10`; // Ensure limit is explicitly set or default to 10

        // Append filters to the URL if they are active
        if (currentFilters.liked && userObjectId) {
          // Send the user's MongoDB ObjectId to the backend for the 'likedBy' filter
          apiUrl += `&likedBy=${userObjectId}`;
        }
        if (currentFilters.diveType) {
          apiUrl += `&diveType=${encodeURIComponent(currentFilters.diveType)}`;
        }
        if (currentFilters.city) {
          apiUrl += `&city=${encodeURIComponent(currentFilters.city)}`;
        }
        if (currentFilters.state) {
          apiUrl += `&state=${encodeURIComponent(currentFilters.state)}`;
        }
        console.log(`[fetchDivesites] Constructed API URL: ${apiUrl}`);

        const response = await fetch(apiUrl, payloadHeader);
        const json = await response.json();
        console.log(
          `[fetchDivesites API Response] for page ${fetchPageNumber}:`,
          json
        );

        // Check if API response is successful and contains valid data
        if (response.ok && json.divesites && Array.isArray(json.divesites)) {
          // Determine if we should clear existing divesites (e.g., for a new search/filter)
          const shouldClearAllDivesites = fetchPageNumber === 1;

          const newDivesites = json.divesites;
          console.log(
            `[fetchDivesites] New divesites fetched on this page:`,
            newDivesites
          );

          // Hydrate new divesites with 'isLikedByUser' status for UI (heart icon)
          // This is still necessary even if the backend is filtering, as it adds
          // the UI specific 'isLikedByUser' property to each returned divesite object.
          const hydratedNewDivesites = await Promise.all(
            newDivesites.map(async (site) => {
              let isLikedByUser = false;
              if (userObjectId) {
                // Only check if a user is logged in (has userObjectId)
                try {
                  // checkItemLiked is assumed to be a frontend utility that checks if a user liked a specific item
                  isLikedByUser = await checkItemLiked(userObjectId, site._id);
                } catch (e) {
                  console.error(`[checkItemLiked Error] for ${site._id}:`, e);
                  isLikedByUser = false; // Default to false on error
                }
              }
              return { ...site, isLikedByUser };
            })
          );
          console.log(
            `[fetchDivesites] Hydrated new divesites (with isLikedByUser):`,
            hydratedNewDivesites
          );

          setState((prev) => {
            const updatedAllDivesites = shouldClearAllDivesites
              ? hydratedNewDivesites // Start fresh for page 1 or filter change
              : [...prev.allDivesites, ...hydratedNewDivesites]; // Append for subsequent pages

            // Determine if there are more pages based on the number of divesites returned
            const hasMorePages = json.divesites.length === 10; // Assuming backend limit is 10

            console.log(
              `[fetchDivesites END] Has more pages to load: ${hasMorePages}`
            );
            console.log(
              `[fetchDivesites END] Total allDivesites after this fetch: ${updatedAllDivesites.length}`
            );

            return {
              ...prev,
              allDivesites: updatedAllDivesites,
              hasMore: hasMorePages,
              initialLoading: false, // Initial load complete
              loading: false, // Loading finished
              isLoadMorePending: false, // Load more request complete
            };
          });

          // Dispatch the *newly fetched and hydrated* divesites to the context.
          // The context reducer (useDivesitesContext) should be designed to append or replace as needed.
          // For backend filtering, the context should ideally reflect the currently loaded (and filtered) items.
          dispatch({ type: "SET_DIVESITES", payload: hydratedNewDivesites });
        } else {
          // Handle API response errors or invalid data
          console.error(
            `[fetchDivesites Error] API response not OK or invalid data for page ${fetchPageNumber}:`,
            json
          );
          setState((prev) => ({
            ...prev,
            error:
              json.message || "API request failed or returned invalid data",
            hasMore: false,
            initialLoading: false,
            loading: false,
            isLoadMorePending: false,
          }));
          dispatch({ type: "SET_DIVESITES", payload: [] }); // Clear divesites in context on error
        }
      } catch (e) {
        // Handle network errors or other exceptions during fetch
        console.error(
          `[fetchDivesites Caught Error] for page ${fetchPageNumber}:`,
          e
        );
        setState((prev) => ({
          ...prev,
          error: e.message,
          hasMore: false,
          initialLoading: false,
          loading: false,
          isLoadMorePending: false,
        }));
        dispatch({ type: "SET_DIVESITES", payload: [] }); // Clear divesites in context on error
      }
    },
    [loading, allDivesites, userObjectId, userId, authContext, filter, dispatch] // Dependencies
  );

  // --- Effect for Initial Data Load / Filter Changes ---
  // This useEffect triggers a fetch when the component mounts, or when filters are reset.
  useEffect(() => {
    // Only fetch if initialLoading is true (meaning a fresh load is required)
    // and not currently loading.
    if (initialLoading && !loading) {
      console.log(
        "[useEffect-initialLoad] Triggering initial fetch with current filters..."
      );
      fetchDivesites(1, filter); // Always fetch the first page with the current filter state
    }
  }, [fetchDivesites, initialLoading, loading, filter]); // 'filter' is a dependency to re-trigger when filters change

  // --- Effect to Extract Filter Options for UI Dropdowns ---
  // This effect processes `allDivesites` to populate unique filter options
  // (e.g., for dropdowns), allowing users to select from available cities/types.
  useEffect(() => {
    if (allDivesites.length > 0) {
      console.log(
        `[useEffect-filterOptions] Updating filter options from ${allDivesites.length} divesites.`
      );
      const diveTypesSet = new Set();
      const citiesSet = new Set();
      const statesSet = new Set();

      for (const divesite of allDivesites) {
        const diveType = divesite?.uddf?.divesite?.site?.environment;
        if (diveType) diveTypesSet.add(diveType);

        const city = divesite?.uddf?.divesite?.site?.geography?.address?.city;
        if (city) citiesSet.add(city);

        const state =
          divesite?.uddf?.divesite?.site?.geography?.address?.province;
        if (state) statesSet.add(state);
      }

      setFilterOptions({
        uniqueDiveTypes: Array.from(diveTypesSet).sort(),
        uniqueCities: Array.from(citiesSet).sort(),
        uniqueStates: Array.from(statesSet).sort(),
      });
    } else {
      console.log(
        "[useEffect-filterOptions] allDivesites is empty, not updating filter options."
      );
      // Clear options if no divesites are loaded (e.g., after filter clear or error)
      setFilterOptions({
        uniqueDiveTypes: [],
        uniqueCities: [],
        uniqueStates: [],
      });
    }
  }, [allDivesites]); // Re-run when `allDivesites` changes

  // --- Load More Logic for Infinite Scrolling ---
  const loadMore = useCallback(() => {
    // Only load more if there are more pages, not already loading, and no pending load-more request
    if (hasMore && !loading && !isLoadMorePending) {
      console.log(
        `[loadMore] Triggering load more for page ${pageNumber + 1}.`
      );
      setState((prev) => ({
        ...prev,
        isLoadMorePending: true, // Set flag to prevent double-triggering
        pageNumber: prev.pageNumber + 1, // Increment page number for the next fetch
      }));
      // Call fetchDivesites with the *next* page number, passing the *current* filters
      fetchDivesites(pageNumber + 1, filter);
    } else {
      console.log("[loadMore] Conditions not met for loading more:", {
        hasMore,
        loading,
        isLoadMorePending,
        isFilterActive, // Note: isFilterActive doesn't block loadMore directly, but helps context
      });
    }
  }, [
    hasMore,
    loading,
    isLoadMorePending,
    pageNumber,
    fetchDivesites,
    filter, // `filter` is a dependency here to ensure loadMore uses the latest filter state
  ]);

  // --- Filter State Management Handlers ---
  const handleFilterChange = useCallback((event, value, fieldName) => {
    const newFilterValue = value === null ? "" : value;

    setFilter((prevFilter) => {
      const updatedFilter = {
        ...prevFilter,
        [fieldName]: newFilterValue,
      };
      console.log(
        `[handleFilterChange] Set filter ${fieldName} to:`,
        newFilterValue
      );
      console.log(`[handleFilterChange] New filter state:`, updatedFilter);

      // CRITICAL: Reset pagination state when a filter changes
      // This ensures a fresh fetch from page 1 with the new filters
      setState((prev) => ({
        ...prev,
        allDivesites: [], // Clear existing data to show new filtered results
        pageNumber: 1, // Reset to the first page
        hasMore: true, // Assume there are more pages for the new filter until proven otherwise
        initialLoading: true, // Mark as initial load to trigger the useEffect to fetch
        isLoadMorePending: false, // Clear any pending load more requests
      }));

      // The useEffect watching `initialLoading` and `filter` will now trigger
      // `fetchDivesites(1, updatedFilter)` due to `initialLoading: true`.
      return updatedFilter;
    });
  }, []);

  const handleClearFilters = useCallback(() => {
    console.log("[handleClearFilters] Clearing all filters.");
    // Reset filter state to defaults
    setFilter({ diveType: "", city: "", state: "", liked: false });

    // CRITICAL: Reset pagination state when filters are cleared
    // This triggers a fresh fetch of unfiltered data from page 1
    setState((prev) => ({
      ...prev,
      allDivesites: [],
      pageNumber: 1,
      hasMore: true,
      initialLoading: true, // Mark as a new initial load
      isLoadMorePending: false,
    }));
  }, []);

  // --- Return values from the hook ---
  return {
    divesites: contextDivesites || [], // The list of divesites currently in context (from fetched data)
    loading,
    initialLoading,
    error,
    hasMore,
    isFilterActive,
    filter, // Current filter state for UI components (e.g., input values)
    filterOptions, // Options for filter dropdowns (derived from loaded data)
    allDivesites, // The accumulated list of divesites loaded from the backend (already filtered)
    loadMore, // Function to trigger loading of the next page
    handleFilterChange, // Function to update filters and trigger re-fetch
    handleClearFilters, // Function to clear filters and trigger re-fetch
  };
};

export default useDivesites;
